import { useRef } from "react";
import { useQuery } from "react-query";
import { doNothing } from "@edgetier/utilities";
import { IUseUpdateCheckerProps } from "./use-update-checker.types";

/**
 * Fetch a fingerprint for the currently deployed `index.html`, bypassing the browser and CDN
 * caches so the response always reflects the latest deployment.
 * @returns The `etag` header, falling back to `last-modified` when no etag is present.
 */
export const fetchEtag = async () => {
    const res = await fetch("index.html", {
        method: "HEAD",
        cache: "no-store",
        headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
        },
    });
    return res.headers.get("etag") ?? res.headers.get("last-modified");
};

/**
 * Poll `index.html` at a fixed interval and invoke a callback when its fingerprint changes,
 * indicating a new app version has been deployed.
 * @param intervalMs        How often to poll, in milliseconds.
 * @param onUpdateAvailable Called once when a new deployment is detected.
 * @param enabled           Whether polling is active.
 */
export const useUpdateChecker = ({ intervalMs, onUpdateAvailable, enabled = true }: IUseUpdateCheckerProps) => {
    const initialEtag = useRef<string | null>(null);

    useQuery("update-checker", fetchEtag, {
        enabled,
        refetchInterval: intervalMs,
        // Keep polling while the tab is backgrounded so a deployed update is detected (and the
        // user notified) even if they don't have Watchtower focused at the time.
        refetchIntervalInBackground: true,
        // Fail silently: this check is best-effort, so a failed poll (e.g. offline or a transient
        // network error) should not surface the global error toast. Overriding onError here stops
        // the default showServerError handler from running; the next interval simply retries.
        onError: doNothing,
        onSuccess: (etag) => {
            if (!etag) return;

            if (initialEtag.current === null) {
                initialEtag.current = etag;
                return;
            }

            if (etag !== initialEtag.current) {
                onUpdateAvailable();
                initialEtag.current = etag;
            }
        },
    });
};
